// V9.23 · Razão Único — lançamentos, DRE, caixa, fornecedores e categorias.
// Pagamentos geram lançamentos automáticos (travados); o resto é manual, no mesmo razão.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Plus, Search, Edit, Trash2, TrendingUp, TrendingDown, Wallet, Lock,
  RefreshCw, Landmark, Percent, Loader2,
} from 'lucide-react';
import LancamentoModal from './Financeiro/LancamentoModal';
import FornecedoresTab from './Financeiro/FornecedoresTab';
import CategoriasTab from './Financeiro/CategoriasTab';
import ContasPagarTab from './Financeiro/ContasPagarTab';
import DreAnualTab from './Financeiro/DreAnualTab';
import LembretesBanner from './Financeiro/LembretesBanner';
import AdquirentesTab from './Financeiro/AdquirentesTab';
import ReceberTab from './Financeiro/ReceberTab';
import ProjecaoTab from './Financeiro/ProjecaoTab';
import OrcamentoTab from './Financeiro/OrcamentoTab';
import ResumoMesCard from './Financeiro/ResumoMesCard';

const money = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const TIPO_STYLE = {
  RECEITA: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  DEDUCAO: 'bg-amber-100 text-amber-800 border-amber-300',
  DESPESA: 'bg-rose-100 text-rose-800 border-rose-300',
  TRANSFERENCIA: 'bg-sky-100 text-sky-800 border-sky-300',
  AJUSTE: 'bg-slate-100 text-slate-800 border-slate-300',
};

function lastMonths(n = 24) {
  const out = [];
  const d = new Date();
  for (let i = 0; i < n; i += 1) {
    out.push(`${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

const DreCard = ({ label, value, sub, tone = 'default', icon: Icon, testid }) => {
  const tones = {
    default: 'border-border', good: 'border-emerald-300 bg-emerald-50/60',
    warn: 'border-amber-300 bg-amber-50/60', bad: 'border-rose-300 bg-rose-50/60',
    result: 'border-foreground/30 bg-foreground/5',
  };
  return (
    <div className={`rounded-xl border p-4 transition-transform hover:-translate-y-0.5 ${tones[tone]}`}
      data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <p className="text-2xl font-bold mt-1 tabular-nums">{money(value)}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
};

export default function FinancialEntries() {
  const [tab, setTab] = useState('lancamentos');
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [sum, setSum] = useState(null);
  const [planoContas, setPlanoContas] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [segmentos, setSegmentos] = useState([]);
  const [centrosCusto, setCentrosCusto] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const meses = useMemo(() => lastMonths(24), []);
  const [competencia, setCompetencia] = useState(meses[0]);
  const [fTipo, setFTipo] = useState('all');
  const [fUnidade, setFUnidade] = useState('all');
  const [fSeg, setFSeg] = useState('all');
  const [fCC, setFCC] = useState('all');
  const [fOrigem, setFOrigem] = useState('all');
  const [q, setQ] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadCadastros = useCallback(async () => {
    const [pc, un, sg, cc, acc] = await Promise.all([
      api.get('/fin/plano-contas').catch(() => ({ data: [] })),
      api.get('/fin/unidades').catch(() => ({ data: [] })),
      api.get('/fin/segmentos').catch(() => ({ data: [] })),
      api.get('/fin/centros-custo').catch(() => ({ data: [] })),
      api.get('/financial/accounts/receiving').catch(() => ({ data: { accounts: [] } })),
    ]);
    setPlanoContas(pc.data || []);
    setUnidades(un.data || []);
    setSegmentos(sg.data || []);
    setCentrosCusto(cc.data || []);
    setAccounts(acc.data.accounts || []);
  }, []);

  const loadLedger = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        competencia,
        tipo: fTipo !== 'all' ? fTipo : undefined,
        cod_unidade: fUnidade !== 'all' ? fUnidade : undefined,
        cod_seg: fSeg !== 'all' ? fSeg : undefined,
        cod_cc: fCC !== 'all' ? fCC : undefined,
        origem: fOrigem !== 'all' ? fOrigem : undefined,
        q: q || undefined,
      };
      const [e, s] = await Promise.all([
        api.get('/fin/ledger/entries', { params }),
        api.get('/fin/ledger/summary', {
          params: {
            competencia,
            cod_unidade: params.cod_unidade, cod_seg: params.cod_seg, cod_cc: params.cod_cc,
          },
        }),
      ]);
      setEntries(e.data.items || []);
      setSum(s.data);
    } catch (err) {
      toast.error('Erro ao carregar o razão financeiro.');
    } finally { setLoading(false); }
  }, [competencia, fTipo, fUnidade, fSeg, fCC, fOrigem, q]);

  useEffect(() => { loadCadastros(); }, [loadCadastros]);
  useEffect(() => { const t = setTimeout(loadLedger, 250); return () => clearTimeout(t); }, [loadLedger]);

  const contaLabel = useMemo(() => {
    const map = {};
    planoContas.forEach((c) => { map[c.cod_conta] = c.descricao; });
    return map;
  }, [planoContas]);

  const excluir = async (row) => {
    try {
      const { data } = await api.delete(`/fin/ledger/entries/${row.id}`);
      toast.success(data.message || 'Lançamento excluído.');
      loadLedger();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao excluir.');
    }
  };

  const dre = sum?.dre;

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="pagina-razao-unico">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Landmark className="w-6 h-6" /> Lançamentos Financeiros
          </h1>
          <p className="text-sm text-muted-foreground">
            Razão único da escola · pagamentos entram automaticamente, despesas você lança aqui
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => { loadCadastros(); loadLedger(); }}
            data-testid="btn-atualizar">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Atualizar
          </Button>
          <Button onClick={() => { setEditing(null); setModalOpen(true); }} data-testid="btn-novo-lancamento">
            <Plus className="w-4 h-4 mr-1" /> Novo lançamento
          </Button>
        </div>
      </div>

      {/* ---------- Lembretes de vencimento ---------- */}
      <LembretesBanner refreshKey={refreshKey} onVerContasPagar={() => setTab('contas-pagar')} />

      {/* ---------- Cards de DRE ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3" data-testid="cards-dre">
        <DreCard label="Receita bruta" value={dre?.receita_bruta} icon={TrendingUp} tone="good"
          testid="card-receita-bruta"
          sub={`${dre?.qtd_lancamentos || 0} lançamentos · ${dre?.qtd_automaticos || 0} automáticos`} />
        <DreCard label="(−) Deduções" value={dre?.deducoes} icon={Percent} tone="warn"
          testid="card-deducoes" sub="Taxas de cartão, bolsas, estornos" />
        <DreCard label="= Receita líquida" value={dre?.receita_liquida} tone="default"
          testid="card-receita-liquida" />
        <DreCard label="(−) Despesas" value={dre?.despesas} icon={TrendingDown} tone="bad"
          testid="card-despesas"
          sub={`Fixas ${money(dre?.despesas_fixas)} · Variáveis ${money(dre?.despesas_variaveis)}`} />
        <DreCard label="= Resultado" value={dre?.resultado} tone="result" testid="card-resultado"
          sub={`Margem ${dre?.margem_percent ?? 0}% · ${competencia}`} />
      </div>

      {/* ---------- Caixa ---------- */}
      <Card data-testid="card-caixa">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Wallet className="w-4 h-4" /> Caixa por conta <span className="text-xs font-normal text-muted-foreground">(saldo acumulado)</span>
          </CardTitle>
          <p className="text-lg font-bold tabular-nums" data-testid="caixa-total">
            {money(sum?.caixa?.total)}
          </p>
        </CardHeader>
        <CardContent>
          {!sum?.caixa?.contas?.length && (
            <p className="text-sm text-muted-foreground">Nenhuma movimentação registrada ainda.</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {(sum?.caixa?.contas || []).map((c) => (
              <div key={c.account_id || 'none'} className="rounded-lg border p-3"
                data-testid={`caixa-conta-${c.account_id || 'sem-conta'}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium truncate">{c.nome}</p>
                  {c.is_cash && <Badge variant="secondary" className="text-[10px]">Dinheiro</Badge>}
                </div>
                <p className={`text-lg font-bold tabular-nums ${c.saldo < 0 ? 'text-rose-600' : ''}`}>
                  {money(c.saldo)}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  + {money(c.entradas)} · − {money(c.deducoes + c.saidas)}
                  {c.transferencias ? ` · transf. ${money(c.transferencias)}` : ''}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full sm:w-auto overflow-x-auto justify-start" data-testid="razao-tabs">
          <TabsTrigger value="lancamentos" data-testid="tab-btn-lancamentos">Lançamentos</TabsTrigger>
          <TabsTrigger value="contas-pagar" data-testid="tab-btn-contas-pagar">Contas a Pagar</TabsTrigger>
          <TabsTrigger value="contas-receber" data-testid="tab-btn-contas-receber">Contas a Receber</TabsTrigger>
          <TabsTrigger value="projecao" data-testid="tab-btn-projecao">Projeção de Caixa</TabsTrigger>
          <TabsTrigger value="orcamento" data-testid="tab-btn-orcamento">Orçamento</TabsTrigger>
          <TabsTrigger value="adquirentes" data-testid="tab-btn-adquirentes">Adquirentes</TabsTrigger>
          <TabsTrigger value="dre-anual" data-testid="tab-btn-dre-anual">DRE Comparativo</TabsTrigger>
          <TabsTrigger value="fornecedores" data-testid="tab-btn-fornecedores">Fornecedores</TabsTrigger>
          <TabsTrigger value="categorias" data-testid="tab-btn-categorias">Categorias & CC</TabsTrigger>
        </TabsList>

        <TabsContent value="lancamentos" className="space-y-3">
          <Card>
            <CardContent className="p-3 flex flex-wrap gap-2 items-center">
              <Select value={competencia} onValueChange={setCompetencia}>
                <SelectTrigger className="w-32" data-testid="filtro-competencia"><SelectValue /></SelectTrigger>
                <SelectContent>{meses.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={fTipo} onValueChange={setFTipo}>
                <SelectTrigger className="w-40" data-testid="filtro-tipo"><SelectValue placeholder="Tipo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  {['RECEITA', 'DEDUCAO', 'DESPESA', 'TRANSFERENCIA', 'AJUSTE'].map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={fOrigem} onValueChange={setFOrigem}>
                <SelectTrigger className="w-40" data-testid="filtro-origem"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toda origem</SelectItem>
                  <SelectItem value="automatico">Automático</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
              <Select value={fUnidade} onValueChange={setFUnidade}>
                <SelectTrigger className="w-40" data-testid="filtro-unidade"><SelectValue placeholder="Unidade" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as unidades</SelectItem>
                  {unidades.map((u) => <SelectItem key={u.cod_unidade} value={u.cod_unidade}>{u.nome_unidade}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={fSeg} onValueChange={setFSeg}>
                <SelectTrigger className="w-40" data-testid="filtro-segmento"><SelectValue placeholder="Segmento" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os segmentos</SelectItem>
                  {segmentos.map((s) => <SelectItem key={s.cod_seg} value={s.cod_seg}>{s.nome_seg}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={fCC} onValueChange={setFCC}>
                <SelectTrigger className="w-40" data-testid="filtro-cc"><SelectValue placeholder="Centro de custo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os CC</SelectItem>
                  {centrosCusto.map((c) => <SelectItem key={c.cod_cc} value={c.cod_cc}>{c.nome_cc}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="relative flex-1 min-w-[180px]">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
                <Input className="pl-8" placeholder="Buscar histórico, documento, fornecedor"
                  value={q} onChange={(e) => setQ(e.target.value)} data-testid="filtro-busca" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Data</TableHead><TableHead>Tipo</TableHead><TableHead>Histórico</TableHead>
                  <TableHead>Categoria</TableHead><TableHead>CC</TableHead><TableHead>Conta</TableHead>
                  <TableHead className="text-right">Valor</TableHead><TableHead>Origem</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow><TableCell colSpan={9} className="text-center py-8">Carregando…</TableCell></TableRow>
                  )}
                  {!loading && !entries.length && (
                    <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">
                      Nenhum lançamento em {competencia}.
                    </TableCell></TableRow>
                  )}
                  {!loading && entries.map((r) => (
                    <TableRow key={r.id} data-testid={`lanc-row-${r.id}`}>
                      <TableCell className="whitespace-nowrap text-xs">{(r.data || '').slice(0, 10)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={TIPO_STYLE[r.tipo] || ''}>{r.tipo || '—'}</Badge>
                        {!r.in_dre && r.tipo && !['TRANSFERENCIA', 'AJUSTE'].includes(r.tipo) && (
                          <span className="ml-1 text-[10px] text-muted-foreground">fora do DRE</span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[280px]">
                        <p className="truncate">{r.historico || '—'}</p>
                        {r.fornecedor_cliente && (
                          <p className="text-[11px] text-muted-foreground truncate">{r.fornecedor_cliente}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.cod_conta ? `${r.cod_conta} · ${contaLabel[r.cod_conta] || ''}` : '—'}
                      </TableCell>
                      <TableCell className="text-xs">{r.cod_cc || '—'}</TableCell>
                      <TableCell className="text-xs">{r.account_name || '—'}</TableCell>
                      <TableCell className={`text-right font-medium tabular-nums ${
                        r.tipo === 'RECEITA' ? 'text-emerald-700' : r.tipo === 'DESPESA' || r.tipo === 'DEDUCAO' ? 'text-rose-700' : ''}`}>
                        {money(Math.abs(r.valor))}
                      </TableCell>
                      <TableCell>
                        {r.origem === 'automatico' ? (
                          <Badge variant="secondary" className="gap-1" data-testid={`lanc-auto-${r.id}`}>
                            <Lock className="w-3 h-3" /> Automático · pagamento
                          </Badge>
                        ) : <Badge variant="outline">Manual</Badge>}
                        {r.payment_status === 'previsto' && (
                          <Badge variant="outline" className="ml-1 bg-amber-100 text-amber-800 border-amber-300"
                            data-testid={`lanc-previsto-${r.id}`}>
                            Previsto · vence {r.due_date}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {r.locked ? (
                          <span className="text-[11px] text-muted-foreground">estorne na origem</span>
                        ) : (
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" data-testid={`lanc-editar-${r.id}`}
                              onClick={() => { setEditing(r); setModalOpen(true); }}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600"
                              data-testid={`lanc-excluir-${r.id}`} onClick={() => excluir(r)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {!!sum?.top_despesas?.length && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Maiores despesas de {competencia}</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {sum.top_despesas.map((d) => (
                  <div key={d.cod_conta} className="flex justify-between border rounded px-3 py-2 text-sm">
                    <span className="truncate">{d.descricao}</span>
                    <span className="font-medium tabular-nums">{money(d.valor)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="contas-pagar">
          <ContasPagarTab accounts={accounts}
            onChanged={() => { loadLedger(); setRefreshKey((k) => k + 1); }} />
        </TabsContent>

        <TabsContent value="contas-receber">
          <ReceberTab competencia={competencia} />
        </TabsContent>

        <TabsContent value="projecao"><ProjecaoTab /></TabsContent>

        <TabsContent value="orcamento">
          <OrcamentoTab competencia={competencia} planoContas={planoContas} />
        </TabsContent>

        <TabsContent value="adquirentes"><AdquirentesTab /></TabsContent>

        <TabsContent value="dre-anual" className="space-y-3">
          <ResumoMesCard />
          <DreAnualTab filtros={{
            cod_unidade: fUnidade !== 'all' ? fUnidade : undefined,
            cod_seg: fSeg !== 'all' ? fSeg : undefined,
            cod_cc: fCC !== 'all' ? fCC : undefined,
          }} />
        </TabsContent>

        <TabsContent value="fornecedores"><FornecedoresTab /></TabsContent>

        <TabsContent value="categorias">
          <CategoriasTab planoContas={planoContas} centrosCusto={centrosCusto} onReload={loadCadastros} />
        </TabsContent>
      </Tabs>

      <LancamentoModal open={modalOpen} onOpenChange={setModalOpen} entry={editing}
        onSaved={() => { loadLedger(); setRefreshKey((k) => k + 1); }}
        planoContas={planoContas} unidades={unidades}
        segmentos={segmentos} centrosCusto={centrosCusto} accounts={accounts} />
    </div>
  );
}
